import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Skeleton, Box, Divider, Tabs, Tab, alpha, useTheme } from '@mui/material';
import { Card } from '@/components/card';
import { SuspenseAndErrorBoundary } from '@/components/security';
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { useOne } from '@refinedev/core';
import { useState } from 'react';
import { useParsed } from "@refinedev/core";

import { analyticsOrderData } from '@/utils/data-provider/data';
import palette from '@/palette';

// Define metric types for tab selection
type MetricType = 'count' | 'total_sales' | 'total_quantity';

// Define a type for the possible statuses
type StatusType = 'pending' | 'confirmed' | 'shipped' | 'cancelled' | 'delivered';

export const OrderStatusMetricsChart = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [metricType, setMetricType] = useState<MetricType>('count');
  
  let { data: responseData, isLoading } = useOne({
    resource: "analytics",
    id: "orders",
  });

  let data = responseData?.data || { orders_by_status: [] };
  const params = useParsed();
  if (params.pathname == '/dummy-dashboard') {
    isLoading = false;
    // @ts-ignore
    data = analyticsOrderData;
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: MetricType) => {
    setMetricType(newValue);
  };

  // Status display configuration with appropriate colors
  const statusConfig: Record<StatusType, { color: string; label: string }> = {
    'pending': { color: palette.dark.warning.main, label: 'Pending' },
    'confirmed': { color: palette.dark.success.main, label: 'Confirmed' },
    'shipped': { color: palette.dark.primary.main, label: 'Shipped' },
    'cancelled': { color: palette.dark.error.main, label: 'Cancelled' },
    'delivered': { color: palette.dark.info.main, label: 'Delivered' }
  };

  // Memoize chart options and series to prevent unnecessary recalculations
  const { chartOptions, series } = useMemo(() => {
    if (!data || !data.orders_by_status) {
      return { 
        chartOptions: {}, 
        series: [],
        total: 0
      };
    }

    // const metricLabels = {
    //   count: 'Orders',
    //   grand_total_amount: 'Amount',
    //   grand_total_quantity: 'Quantity'
    // };

    const metricFormatters = {
      count: (val: number) => Math.round(val).toString(),
      total_sales: (val: number) => new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
      }).format(val),
      total_quantity: (val: number) => Math.round(val).toString() + ' pcs'
    };
    
    // Extract values based on selected metric type
    const values = data.orders_by_status.map((item: any) => item[metricType]);
    const statuses = data.orders_by_status.map((item: any) => {
      const status = item.status.toLowerCase() as StatusType;
      return statusConfig[status]?.label || item.status;
    });
    
    // Calculate total for the selected metric
    const totalValue = values.reduce((sum: number, current: number) => sum + current, 0);

    // Define colors based on status
    const statusColors = data.orders_by_status.map((item: any) => {
      const status = item.status.toLowerCase() as StatusType;
      return statusConfig[status]?.color || palette.dark.primary.main;
    });

    // Create fill colors with reduced opacity
    const fillColors = statusColors.map((color: string) => {
      return alpha(color, 0.6);
    });

    const options: ApexOptions = {
      chart: {
        type: 'donut',
        background: 'transparent',
        fontFamily: 'inherit',
        animations: {
          enabled: true,
          speed: 500,
          animateGradually: {
            enabled: true,
            delay: 150
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        },
      },
      colors: fillColors,
      stroke: {
        width: 3,
        colors: statusColors,
        curve: 'smooth',
      },
      labels: statuses,
      plotOptions: {
        pie: {
          donut: {
            size: '55%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '14px',
                fontWeight: 'bold',
                offsetY: -10,
                color: isDarkMode ? palette.dark.grey[200] : palette.light.grey[800],
              },
              value: {
                color: isDarkMode ? palette.dark.grey[200] : palette.light.grey[800],
                show: true,
                fontSize: '16px',
                fontWeight: 'bold',
                formatter: (val) => metricFormatters[metricType](parseFloat(val))
              },
              total: {
                color: isDarkMode ? palette.dark.grey[200] : palette.light.grey[800], 
                show: true,
                label: 'Total',
                fontSize: '14px',
                fontWeight: 'bold',
                formatter: () => metricFormatters[metricType](totalValue)
              }
            }
          }
        }
      },
      states: {
        hover: {
          filter: {
            type: 'darken',
            // value: 0.15
          }
        },
        active: {
          filter: {
            type: 'darken',
            // value: 0.2
          }
        }
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '12px',
        fontFamily: 'inherit',
        labels: {
          colors: isDarkMode ? palette.dark.grey[200] : palette.light.grey[800],
        },
        markers: {
          size: 8,
          strokeWidth: 0,
          fillColors: statusColors,
          shape: 'square'
          // strokeColor: '#fff',
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5
        }
      },
      tooltip: {
        theme: theme.palette.mode,
        y: {
          formatter: (val) => {
            const label = metricType === 'count' ? 'Orders' : 
                         metricType === 'total_sales' ? 'Amount' : 'Quantity';
            return `${label}: ${metricFormatters[metricType](val)}`;
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      responsive: [{
        breakpoint: 480,
        options: {
          legend: {
            position: 'bottom',
            fontSize: '10px',
          }
        }
      }]
    };

    const seriesData = values;

    return { 
      chartOptions: options, 
      series: seriesData,
      total: totalValue
    };
  }, [data, metricType]);

  // const metricCards = [
  //   { id: 'count', label: 'Order Count', formatter: (val: number) => Math.round(val).toString() },
  //   { id: 'grand_total_amount', label: 'Order Value', formatter: (val: number) => new Intl.NumberFormat('en-IN', {
  //     style: 'currency',
  //     currency: 'INR',
  //     maximumFractionDigits: 0,
  //     notation: 'compact'
  //   }).format(val) },
  //   { id: 'grand_total_quantity', label: 'Quantity', formatter: (val: number) => Math.round(val).toString() + ' pcs' }
  // ];

  // // Predefined status colors for legend
  // const statusLegendItems = [
  //   { status: 'Pending', color: palette.dark.warning.main },
  //   { status: 'Dispatched', color: palette.dark.success.main },
  //   { status: 'Canceled', color: palette.dark.error.main },
  //   { status: 'Rejected', color: palette.dark.info.main }
  // ];

  return (      
    <Card
      title="Orders by Status"
      icon={<AssessmentOutlinedIcon />}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px',
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '.MuiCardContent-root': {
          flexGrow: 1,
          height: 'auto',
          padding: '16px',
          paddingBottom: '16px !important',
        },
        '&:hover': {
          boxShadow: '0 6px 25px 0 rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)'
        }
      }}
      cardContentProps={{
        sx: {
          height: '350px',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Tabs
        value={metricType}
        onChange={handleTabChange}
        centered
        variant="fullWidth"
        sx={{ mb: 2 }}
      >
        <Tab label="Count" value="count" />
        <Tab label="Value (₹)" value="total_sales" />
        <Tab label="Quantity (Pcs)" value="total_quantity" />
      </Tabs>
      
      <Divider sx={{ mb: 2 }} />
      
      <SuspenseAndErrorBoundary>
        {isLoading ? (
          <Skeleton
            variant="rounded"
            height='100%'
            width='100%'
            animation="wave"
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-around', 
              mb: 2, 
              px: 1 
            }}>
              {metricCards.map((card) => (
                <Box
                  key={card.id}
                  sx={{
                    textAlign: 'center',
                    p: 1,
                    borderRadius: '8px',
                    bgcolor: card.id === metricType ? 'rgba(0,0,0,0.05)' : 'transparent',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.05)'
                    }
                  }}
                  onClick={() => setMetricType(card.id as MetricType)}
                >
                  <Typography variant="body2" color="text.secondary">
                    {card.label}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {card.formatter(total)}
                  </Typography>
                </Box>
              ))}
            </Box>
             */}
            <Box sx={{ flexGrow: 1 }}>
              <ReactApexChart
                options={chartOptions}
                series={series}
                type="donut"
                height="100%"
                width="100%"
              />
            </Box>
          </Box>
        )}
      </SuspenseAndErrorBoundary>
    </Card>
  );
};